// Security utilities for input validation and sanitization

export const sanitizeInput = (input: string | null | undefined): string => {
  if (!input) return '';

  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .slice(0, 1000); // Limit length
};

export const sanitizePhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';

  return phone
    .trim()
    .replace(/[^\d+\-\(\)\s]/g, '') // Only allow digits, +, -, (), spaces
    .slice(0, 20); // Reasonable phone number length
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

// Comprehensive list of common weak passwords
const WEAK_PASSWORDS = [
  '123456', '123456789', '12345678', '1234567890', '123123', '12345',
  'password', 'password123', 'password1', 'pass123', 'pass1234',
  'qwerty', 'qwertyuiop', 'qwerty123', 'qwerty1',
  'abc123', 'abc123456', 'abcd1234',
  '111111', '000000', '123321', '654321',
  'admin', 'admin123', 'administrator', 'root', 'rootroot',
  'welcome', 'welcome123', 'welcome1',
  'monkey', 'dragon', 'master', 'letmein', 'login', 'princess',
  'football', 'baseball', 'basketball', 'soccer',
  'iloveyou', 'sunshine', 'shadow', 'ashley', 'bailey',
  'passw0rd', 'p@ssw0rd', 'p@ssword', 'Pa$$w0rd',
  'superman', 'batman', 'trustno1', 'starwars',
  'michael', 'jennifer', 'jordan', 'michelle',
  'qazwsx', 'zxcvbnm', 'asdfgh', 'qweasd',
  '1q2w3e4r', '1qaz2wsx', 'qwertyui',
  'jesus', 'christ', 'ninja', 'mustang', 'access',
  'whatever', 'password!', 'password@', 'password#'
];

export const validatePassword = (password: string): { isValid: boolean; message?: string } => {
  // Minimum length: 6 characters (reasonable for e-commerce)
  if (!password || password.length < 6) {
    return {
      isValid: false,
      message: "הסיסמה חייבת להכיל לפחות 6 תווים"
    };
  }

  // Maximum length: 128 characters (prevent DoS attacks via bcrypt)
  if (password.length > 128) {
    return {
      isValid: false,
      message: "הסיסמה לא יכולה להכיל יותר מ-128 תווים"
    };
  }

  // Complexity requirements - at least 2 out of 4 (more lenient for e-commerce)
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>_\-=+\[\]\\\/~`]/.test(password);

  const complexityCount = [hasUppercase, hasLowercase, hasNumber, hasSpecialChar].filter(Boolean).length;

  // Require at least 2 out of 4 complexity criteria (relaxed from 3/4)
  if (complexityCount < 2) {
    const missing = [];
    if (!hasUppercase) missing.push('אות גדולה');
    if (!hasLowercase) missing.push('אות קטנה');
    if (!hasNumber) missing.push('ספרה');
    if (!hasSpecialChar) missing.push('תו מיוחד (!@#$%^&* וכו\')');

    return {
      isValid: false,
      message: `הסיסמה חייבת להכיל לפחות 2 מתוך 4 הקטגוריות: ${missing.join(', ')}`
    };
  }

  // Check against common weak passwords (case-insensitive)
  const lowerPassword = password.toLowerCase();
  if (WEAK_PASSWORDS.some(weak => lowerPassword.includes(weak))) {
    return {
      isValid: false,
      message: "הסיסמה מכילה מילים נפוצות מדי. נא לבחור סיסמה ייחודית יותר"
    };
  }

  // Check for repeated characters (e.g., "aaaaaa", "111111")
  if (/(.)\1{5,}/.test(password)) {
    return {
      isValid: false,
      message: "הסיסמה לא יכולה להכיל יותר מ-5 תווים זהים ברצף"
    };
  }

  // Check for sequential characters (e.g., "abcdef", "123456")
  const hasSequentialChars = (str: string): boolean => {
    for (let i = 0; i < str.length - 5; i++) {
      const charCode = str.charCodeAt(i);
      let isSequential = true;
      for (let j = 1; j <= 5; j++) {
        if (str.charCodeAt(i + j) !== charCode + j && str.charCodeAt(i + j) !== charCode - j) {
          isSequential = false;
          break;
        }
      }
      if (isSequential) return true;
    }
    return false;
  };

  if (hasSequentialChars(password)) {
    return {
      isValid: false,
      message: "הסיסמה לא יכולה להכיל רצף ארוך של תווים עוקבים"
    };
  }

  // NOTE: Supabase provides additional protection against leaked passwords
  // via the "Leaked Password Protection" feature in Auth settings

  return { isValid: true };
};

export const sanitizeHtml = (input: string): string => {
  // Comprehensive HTML sanitization - remove script tags and event handlers
  return input
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
    .replace(/<object[^>]*>.*?<\/object>/gi, '')
    .replace(/<embed[^>]*>.*?<\/embed>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    .replace(/<style[^>]*>.*?<\/style>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/data:text\/html/gi, '')
    .replace(/data:application\/javascript/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/@import/gi, '')
    .replace(/url\s*\(/gi, '')
    .replace(/eval\s*\(/gi, '')
    .replace(/setTimeout\s*\(/gi, '')
    .replace(/setInterval\s*\(/gi, '')
    .replace(/Function\s*\(/gi, '')
    .replace(/constructor/gi, '')
    .replace(/__proto__/gi, '')
    .replace(/prototype/gi, '')
    .slice(0, 10000); // Prevent extremely large payloads
};

export const validateFileUpload = (file: File): { isValid: boolean; error?: string } => {
  // Define allowed file types and sizes
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  const minSize = 1024; // 1KB minimum

  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: 'רק קבצי תמונה מותרים (JPEG, PNG, WebP, GIF)' };
  }

  if (file.size > maxSize) {
    return { isValid: false, error: 'גודל הקובץ חייב להיות קטן מ-10MB' };
  }

  if (file.size < minSize) {
    return { isValid: false, error: 'גודל הקובץ חייב להיות לפחות 1KB' };
  }

  // Check for suspicious file names
  const suspiciousPatterns = [
    /\.php$/i,
    /\.exe$/i,
    /\.bat$/i,
    /\.sh$/i,
    /\.js$/i,
    /\.html$/i,
    /\.htm$/i,
    /\.jsp$/i,
    /\.asp$/i,
    /\.aspx$/i
  ];

  if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
    return { isValid: false, error: 'שם הקובץ מכיל סיומת לא מותרת' };
  }

  return { isValid: true };
};

export const sanitizeFileName = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace special chars with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^[._]/, '') // Remove leading dots/underscores
    .slice(0, 255); // Limit length
};

export const validateQuoteRequest = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!data.email || !validateEmail(data.email)) {
    errors.push('אימייל תקין נדרש');
  }

  if (!data.requested_quantity || isNaN(data.requested_quantity) || data.requested_quantity <= 0) {
    errors.push('כמות נדרשת חייבת להיות מספר חיובי');
  }

  if (data.phone && !validatePhoneNumber(data.phone)) {
    errors.push('מספר הטלפון אינו תקין');
  }

  if (data.message && data.message.length > 2000) {
    errors.push('ההודעה לא יכולה להכיל יותר מ-2000 תווים');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateSpecialSearchRequest = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!data.email || !validateEmail(data.email)) {
    errors.push('אימייל תקין נדרש');
  }

  if (!data.materials_needed || data.materials_needed.trim().length < 5) {
    errors.push('תיאור החומרים הנדרשים חייב להכיל לפחות 5 תווים');
  }

  if (data.materials_needed && data.materials_needed.length > 1000) {
    errors.push('תיאור החומרים לא יכול להכיל יותר מ-1000 תווים');
  }

  if (data.phone && !validatePhoneNumber(data.phone)) {
    errors.push('מספר הטלפון אינו תקין');
  }

  if (data.budget && (isNaN(data.budget) || data.budget < 0)) {
    errors.push('התקציב חייב להיות מספר חיובי');
  }

  if (data.message && data.message.length > 2000) {
    errors.push('ההודעה לא יכולה להכיל יותר מ-2000 תווים');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const rateLimitKey = (identifier: string, action: string): string => {
  return `ratelimit:${action}:${identifier}`;
};

export const validateProductData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length < 2) {
    errors.push('שם המוצר חייב להכיל לפחות 2 תווים');
  }

  if (data.name && data.name.length > 200) {
    errors.push('שם המוצר לא יכול להכיל יותר מ-200 תווים');
  }

  if (data.description && data.description.trim().length > 0 && data.description.trim().length < 2) {
    errors.push('תיאור המוצר, אם הוזן, חייב להכיל לפחות 2 תווים');
  }

  if (data.price_per_unit && (isNaN(data.price_per_unit) || data.price_per_unit <= 0)) {
    errors.push('מחיר ליח\' חייב להיות מספר חיובי');
  }

  if (data.total_inventory && (isNaN(data.total_inventory) || data.total_inventory < 0)) {
    errors.push('כמות המלאי חייבת להיות מספר חיובי');
  }

  if (data.min_quantity && (isNaN(data.min_quantity) || data.min_quantity < 1)) {
    errors.push('כמות מינימלית חייבת להיות לפחות 1');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateAddressData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // recipient_name and phone are now optional for shipping addresses

  if (!data.city || data.city.trim().length < 2) {
    errors.push('שם העיר חייב להכיל לפחות 2 תווים');
  }

  if (!data.street_name || data.street_name.trim().length < 2) {
    errors.push('שם הרחוב חייב להכיל לפחות 2 תווים');
  }

  if (!data.street_number || data.street_number.trim().length < 1) {
    errors.push('מספר הבית נדרש');
  }

  if (data.phone && !validatePhoneNumber(data.phone)) {
    errors.push('מספר הטלפון אינו תקין');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validatePhoneNumber = (phone: string): boolean => {
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  const phoneRegex = /^(\+972|0)[2-9]\d{7,8}$/;
  return phoneRegex.test(cleanPhone);
};

export const sanitizeNumericInput = (input: string | number): number | null => {
  if (typeof input === 'number') {
    return isNaN(input) ? null : input;
  }

  const cleaned = input.replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};